import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { LenderService } from '@shared/service/lender/lender.service';
import { LoanService } from '@shared/service/loan.service';
import { CrossSellService } from '@shared/service/cross-sell.service';
import {
  CrossSell,
  CrossSellOfferDetails,
  NewCreditOffersEntity,
  CrossSellRequireModule,
  OffersEntity
} from '@shared/model/cross-sell.model';
import { Communication } from '@shared/model/communication.model';
import { StatefulService } from '@app/service/stateful.service';
import { CommonService } from '@workflow/legacy';
import { SawDust, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { CrossSellCardComponent } from './cross-sell-card/cross-sell-card.component';
import { EcpSharedModule } from '@workflow/ecp-shared/shared.module';

@Component({
  selector: 'dx-cross-sell',
  templateUrl: './cross-sell.component.html',
  styleUrls: ['./cross-sell.component.scss'],
  standalone: true,
  imports: [CommonModule, CrossSellCardComponent, SharedUiLibraryModule, EcpSharedModule],
  providers: [CurrencyPipe]
})
export class CrossSellComponent implements OnInit {
  private logger = inject(SawDust).withContext({ component: 'CrossSellComponent' });

  // Using inject pattern for all dependencies
  public readonly lenderService = inject(LenderService);
  public readonly crossSellService = inject(CrossSellService);
  private readonly cp = inject(CurrencyPipe);
  private readonly commonService = inject(CommonService);
  private readonly loanServices = inject(LoanService);
  private readonly statefulService = inject(StatefulService);
  private readonly analyticsMetadataService = inject(AnalyticsMetadataService);
  private readonly googleTrackingService = inject(GoogleTrackingService);

  /** Return loan Number */
  @Input() public appNumber: string;

  /** Specify where to render this component */
  @Input() public displayLocation: string = CrossSellRequireModule.OAOPostApp;

  /** Storage ID */
  public APPLICATION_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/applicationId';

  /** Flag Indicate LDR response API or Mockup */
  // public IS_LDR_API_ENABLED = getMatIconFailedToSanitizeLiteralError;

  /** Indicate showing loader of server request */
  public showLoader = true;

  /** Getting cross sell opertunities for card display */
  public crossSellProducts: CrossSell;

  /** Submitting to L360 cross sell opertunities */
  public objCrossSellOfferDetails: CrossSellOfferDetails;

  /** call back msg change */
  public isDataLoaded = false;

  /** call back msg change */
  public btnCallbackSuccess = false;

  // Slide In panel toggle
  public openSlideInPanel = false;

  // CrossSell modal terms and conditions text
  public termsAndConditionsText: string;

  // Stores Cross-Sell Modal Title
  public docTitle = '';

  // stores pdf data in blob format
  public pdfBlob: Blob;

  public pdfURL: string;

  // Toggles content in crossSell TermsAndConditions modal
  public isContentLoaded = false;
  public stipulationList: string[] = [];
  public showStipulationsOnSlideIn = false;
  /** Polling Count */
  public pollingCount: number;

  /**
   * Load to do items details on Init.
   */
  public ngOnInit(): void {
    this.pollingCount = 0;
    this.startCrossSellPolling();
  }

  /**
   * Function to get loan details.
   */
  private getCrossSellProducts(): void {
    const applicationId = this.statefulService.wrap(this.APPLICATION_ID_PATH).valueString;
    this.appNumber = this.appNumber ? this.appNumber : applicationId;

    if (this.appNumber) {
      this.crossSellService.GetCrossSellConfiguration(this.appNumber).subscribe(
        (response) => {
          if (response?.isSuccess) {
            this.crossSellProducts = response.data;
            if (this.crossSellProducts && (this.crossSellProducts.newCreditOffers || this.crossSellProducts.refinanceOpportunities)) {
              this.crossSellProducts.newCreditOffers = this.crossSellProducts.newCreditOffers.filter((n) => this.crossSellFilter(n));
              this.crossSellProducts.refinanceOpportunities = this.crossSellProducts.refinanceOpportunities.filter((r) =>
                this.crossSellFilter(r)
              );
              const crossSellProductCount =
                this.crossSellProducts.newCreditOffers.length + this.crossSellProducts.refinanceOpportunities.length;
              this.isDataLoaded = crossSellProductCount > 0 ? true : false;
            }
            this.showLoader = false;
          }
        },
        () => {
          this.showLoader = false;
          this.isDataLoaded = false;
        }
      );
    }
  }

  /**
   * Initiates a polling process to retrieve cross-sell products.
   *
   * This private method starts a polling process that repeatedly calls `getCrossSellProducts` at a specified interval.
   * The polling occurs until one of the following conditions is met:
   * 1. New credit offers or refinance opportunities are found within `crossSellProducts`.
   * 2. The maximum number of polling attempts, defined by `POLL_COUNT`, is reached.
   */
  private startCrossSellPolling(): void {
    // Config for polling
    const POLL_COUNT = 5;
    const POLL_INTERVAL_MS = 5000;

    const interval = setInterval(() => {
      this.pollingCount += 1;
      this.getCrossSellProducts();
      if (
        (this.crossSellProducts &&
          (this.crossSellProducts?.newCreditOffers?.length > 0 || this.crossSellProducts?.refinanceOpportunities?.length > 0)) ||
        this.pollingCount >= POLL_COUNT
      ) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
  }

  /**
   * Function sync after loan cta click call
   */
  public onLoanClick(Offers: OffersEntity): void {
    // Cross sell CTA selected
    this.googleTrackingService.publishEvent('arcdx.postapp.cross-sell-cta', {});
    const applicationId = this.statefulService.wrap(this.APPLICATION_ID_PATH).valueString;
    this.appNumber = this.appNumber ? this.appNumber : applicationId;
    if (this.appNumber) {
      this.crossSellService.UpdateCrossSellInterestedStatus(
        {
          applicationId: this.appNumber,
          applicationCrossSellDetailIDs: [Offers.applicationCrossSellDetailID]
        },
        () => {
          if (Offers.successCallBack) {
            Offers.successCallBack();
          }
        },
        (err) => {
          this.logger.errorOnly(err);
          this.showLoader = false;
        }
      );
      this.updateCommunicationNotes(Offers);
    }
  }

  private updateCommunicationNotes(Offers: OffersEntity): void {
    const reqBody: Communication = {
      ApplicationId: this.appNumber,
      IsHighPriority: true,
      IsSticky: true,
      LenderCode: this.lenderService.lenderDataInfo.consumerLoanCode,
      Message: this.getMessage(Offers)
    } as Communication;
    this.crossSellService.SaveCommunicationMessage(reqBody).subscribe(
      (response) => {
        if (response?.isSuccess) {
          // this.btnAcceptedOfferEmit.emit(objAcceptedOffer);
          this.btnCallbackSuccess = true;
          this.showLoader = false;
        }
      },
      (err) => {
        this.logger.errorOnly(err);
        this.showLoader = false;
      }
    );
  }

  public enableScrollbar(): boolean {
    if (this.crossSellProducts && (this.crossSellProducts.newCreditOffers || this.crossSellProducts.refinanceOpportunities)) {
      const crossSellProductCount = this.crossSellProducts.newCreditOffers.length + this.crossSellProducts.refinanceOpportunities.length;
      return crossSellProductCount > 3;
    }
    return false;
  }

  private crossSellFilter(cs: NewCreditOffersEntity): boolean {
    return (
      cs.crossSellStatusDescription &&
      (cs.crossSellStatusDescription.toLocaleLowerCase() === 'interested' || cs.crossSellStatusDescription.toLocaleLowerCase() === 'new') &&
      cs.decisionAmount > 0
    );
  }
  private getMessage(offer: OffersEntity): string {
    const baseMessage = `Online applicant expressed interest in cross sell: ${offer.description}`;
    const amountMessage = this.cp.transform(offer.amount, 'USD', 'symbol', '1.2-2');
    const rateMessage = offer.rate > 0 ? `at ${offer.rate}%` : '';
    const termMessage = offer.term > 0 ? `for ${offer.term} months` : '';

    return `${baseMessage} ${amountMessage} ${rateMessage} ${termMessage}`.trim();
  }

  public openCrossSellSlideInPanel({ crossSellOffer, stipulationList }): void {
    this.pdfURL = null;
    this.isContentLoaded = false;
    this.docTitle = crossSellOffer?.documentLinkDisplayText;
    this.termsAndConditionsText = crossSellOffer.termsAndConditions
      ? this.crossSellService.parseCustomVariable(crossSellOffer.termsAndConditions, crossSellOffer)
      : null;
    if (crossSellOffer.docRepID) this.getCrossSellDocument(crossSellOffer.docRepID);

    this.showStipulationsOnSlideIn = false;
    this.showStipulationsOnSlideIn = this.crossSellService.displayStipulationOnSlideInPanel(stipulationList);
    this.isContentLoaded = true;
    this.stipulationList = stipulationList;
    this.openSlideInPanel = !this.openSlideInPanel;
  }
  private getCrossSellDocument(docRepID: string): void {
    this.loanServices
      .getDocumentByDocRepID(docRepID)
      .then((value) => {
        this.pdfURL = value;
      })
      .catch((err) => {
        this.commonService.errorHandling(err);
      });
  }
}
